import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ComparisonsPage() {
  const { t } = useTranslation();

  const comparisons = [
    {
      title: "AllChinaBuy vs Superbuy",
      slug: "allchinabuy-vs-superbuy",
      desc: "A detailed comparison of fees, shipping, QC, warehouse services, and user experience between AllChinaBuy and Superbuy.",
      features: [
        { feature: "Service Type", acb: "Spreadsheet-focused agent", sb: "Full-service agent" },
        { feature: "QC Photos", acb: "Standard included", sb: "Basic + premium options" },
        { feature: "Shipping Lines", acb: "Multiple options", sb: "Extensive carrier network" },
        { feature: "Community", acb: "Spreadsheet community", sb: "Large user base" },
      ],
    },
    {
      title: "AllChinaBuy vs CSSBuy",
      desc: "Compare AllChinaBuy with CSSBuy to understand differences in pricing, features, and overall service quality.",
      features: [
        { feature: "Pricing", acb: "Competitive", css: "Variable" },
        { feature: "Interface", acb: "Spreadsheet style", css: "Web app" },
        { feature: "Specialization", acb: "Fashion & apparel", css: "General" },
        { feature: "Support", acb: "Community-driven", css: "Ticket-based" },
      ],
    },
    {
      title: "AllChinaBuy vs Sugargoo",
      desc: "See how AllChinaBuy stacks up against Sugargoo in terms of shipping costs, features, and user experience.",
      features: [
        { feature: "Fee Structure", acb: "Transparent", sg: "Tiered" },
        { feature: "Warehouse", acb: "Standard storage", sg: "Extended options" },
        { feature: "Mobile App", acb: "Web-based", sg: "Available" },
        { feature: "Reputation", acb: "Growing", sg: "Established" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-28 md:pt-32 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                {t("sectionComparison")}
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto">
                {t("sectionComparisonDesc")}
              </p>
            </div>

            <div className="space-y-10">
              {comparisons.map((comp, idx) => (
                <div key={idx} className="p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    {comp.title}
                  </h2>
                  <p className="text-sm text-foreground-600 mb-6">{comp.desc}</p>

                  <div className="overflow-x-auto rounded-xl border border-background-200/70 mb-6">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-200/70 bg-background-50">
                          <th className="px-4 py-3 text-left font-semibold text-foreground-800">{t("tableFeature")}</th>
                          <th className="px-4 py-3 text-left font-semibold text-primary-700">{t("tableAllChinaBuy")}</th>
                          <th className="px-4 py-3 text-left font-semibold text-foreground-700">
                            {idx === 0 ? t("tableSuperbuy") : idx === 1 ? t("tableCSSBuy") : t("tableSugargoo")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {comp.features.map((f, fi) => (
                          <tr key={fi} className="border-b border-background-200/50 last:border-0">
                            <td className="px-4 py-3 font-medium text-foreground-800">{f.feature}</td>
                            <td className="px-4 py-3 text-foreground-700">{f.acb}</td>
                            <td className="px-4 py-3 text-foreground-700">{(f as any).sb || (f as any).css || (f as any).sg}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {idx === 0 && (
                    <Link
                      to={`/article/${comp.slug}`}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      {t("readMore")} <i className="ri-arrow-right-line" />
                    </Link>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-10 p-6 md:p-8 rounded-2xl bg-primary-500/10 border border-primary-200/50 text-center">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">Still deciding?</h3>
              <p className="text-foreground-600 mb-6 text-sm">Explore the AllChinaBuy spreadsheet and compare products directly.</p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-block px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors"
              >
                {t("heroCtaPrimary")}
              </a>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}